import { cn } from "@paribus/shared-ui";
import { type VariantProps, cva } from "class-variance-authority";
import { AlertTriangle, CheckCircle, Info, X, XCircle } from "lucide-react";
import * as React from "react";

const bannerVariants = cva("w-full border-l-4 p-4 mb-4 flex items-center justify-between", {
  variants: {
    variant: {
      info: "bg-info-subtle border-info-border",
      warning: "bg-warning-subtle border-warning-border",
      error: "bg-destructive-subtle border-destructive-border",
      success: "bg-success-subtle border-success-border",
    },
  },
  defaultVariants: {
    variant: "info",
  },
});

const iconVariants = cva("h-5 w-5 flex-shrink-0", {
  variants: {
    variant: {
      info: "text-info",
      warning: "text-warning",
      error: "text-destructive",
      success: "text-success",
    },
  },
  defaultVariants: {
    variant: "info",
  },
});

const textVariants = cva("text-sm ml-3", {
  variants: {
    variant: {
      info: "text-info-text",
      warning: "text-warning-text",
      error: "text-destructive-text",
      success: "text-success-text",
    },
  },
  defaultVariants: {
    variant: "info",
  },
});

const closeButtonVariants = cva("ml-4 hover:opacity-70 transition-opacity", {
  variants: {
    variant: {
      info: "text-info hover:text-info-text",
      warning: "text-warning hover:text-warning-text",
      error: "text-destructive hover:text-destructive-text",
      success: "text-success/70 hover:text-success-text",
    },
  },
  defaultVariants: {
    variant: "info",
  },
});

export interface BannerProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof bannerVariants> {
  message: string;
  onDismiss?: () => void;
  dismissible?: boolean;
  icon?: React.ReactNode;
}

const Banner = React.forwardRef<HTMLDivElement, BannerProps>(
  ({ className, variant, message, onDismiss, dismissible = true, icon, ...props }, ref) => {
    const defaultIcon = (() => {
      const iconClassName = iconVariants({ variant });
      switch (variant) {
        case "warning":
          return <AlertTriangle className={iconClassName} />;
        case "error":
          return <XCircle className={iconClassName} />;
        case "success":
          return <CheckCircle className={iconClassName} />;
        case "info":
        default:
          return <Info className={iconClassName} />;
      }
    })();

    return (
      <div ref={ref} className={cn(bannerVariants({ variant }), className)} {...props}>
        <div className="flex">
          <div className="flex-shrink-0">{icon || defaultIcon}</div>
          <div className={textVariants({ variant })}>{message}</div>
        </div>
        {dismissible && onDismiss && (
          <button
            onClick={onDismiss}
            className={closeButtonVariants({ variant })}
            aria-label="Dismiss banner"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
    );
  }
);
Banner.displayName = "Banner";

export { Banner, bannerVariants, textVariants, iconVariants };
